import React from 'react'

import { useTranslation } from 'react-i18next'

import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Rating from '@mui/material/Rating'
import LoadingButton from '@mui/lab/LoadingButton'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'

import { Process, ProcessString } from '../../types/enums/beanProcess'
import { Inventory } from '../../types/inventory'
import Alert from '../alert/alert'
import { DryingSpeed, DryingSpeedString } from '../../types/enums/dryingSpeed'
import { BeanSize, BeanSizeString } from '../../types/enums/beanSize'
import TextInput from './textInput'
import NumberField from '../fields/numberField'
import Select from './select'
import { getBigWeightSuffix, getSingleBigWeightSuffix } from '../../utils/weightUtils'
import ConfirmationDialog from '../dialog/confirmationDialog'

const fieldsetHeaderStyle = {
  fontWeight: 'bold',
  fontSize: '16px',
  color: 'primary.main',
}

// 9 text fields, 2 weight fields, 4 select fields, 2 number fields, 1 date field

interface InventoryFormProps {
  beanInfo: Inventory
  isSaving: boolean
  onSave: (inventoryData: Inventory) => void
  isSettingArchived: boolean
  onSetArchived: (isArchived: boolean) => void
}

const InventoryForm = (props: InventoryFormProps): JSX.Element => {
  const { t } = useTranslation()

  // fields from model
  const [name, setName] = React.useState<string>(props.beanInfo.name || '')
  const [cultivar, setCultivar] = React.useState<string>(props.beanInfo.cultivar || '')
  const [farm, setFarm] = React.useState<string>(props.beanInfo.farm || '')
  const [region, setRegion] = React.useState<string>(props.beanInfo.region || '')
  const [country, setCountry] = React.useState<string>(props.beanInfo.country || '')
  const [exporter, setExporter] = React.useState<string>(props.beanInfo.exporter || '')
  const [importer, setImporter] = React.useState<string>(props.beanInfo.importer || '')
  const [producer, setProducer] = React.useState<string>(props.beanInfo.producer || '')
  const [elevation, setElevation] = React.useState<number>(props.beanInfo.elevation || 0)

  const [beanSize, setBeanSize] = React.useState<BeanSize>(props.beanInfo.bean_size || 0)
  const [dryingSpeed, setDryingSpeed] = React.useState<DryingSpeed>(props.beanInfo.drying_speed || 0)
  const [beanProcess, setBeanProcess] = React.useState<Process>(props.beanInfo.bean_process || 0)
  const [moisture, setMoisture] = React.useState<number>(props.beanInfo.moisture || 0)
  const [waterActivity, setWaterActivity] = React.useState<number>(props.beanInfo.water_activity || 0)
  const [density, setDensity] = React.useState<number>(props.beanInfo.density || 0)
  const [greenBeanColor, setGreenBeanColor] = React.useState<number>(props.beanInfo.green_bean_color || 0)
  const [quality, setQuality] = React.useState<number>(props.beanInfo.quality || 0)

  const [regDate, setRegDate] = React.useState<Date | null>(props.beanInfo.reg_date)
  const [currentWeight, setCurrentWeight] = React.useState<number>(props.beanInfo.current_weight)
  const [initialWeight, setInitialWeight] = React.useState<number>(props.beanInfo.initial_weight)
  const [price, setPrice] = React.useState<number>(props.beanInfo.price || 0)
  const [notes, setNotes] = React.useState<string>(props.beanInfo.notes || '')
  const [isArchiveModalOpen, setIsArchiveModalOpen] = React.useState<boolean>(false)

  // calculated states
  const [isCurrentWeightTooHigh, setIsCurrentWeightTooHigh] = React.useState<boolean>(false)
  const [requiredFieldErrors, setRequiredFieldErrors] = React.useState<Array<keyof Inventory>>([])

  const areFieldsDisabled = props.beanInfo.is_archived

  const saveData = (): void => {
    // validation: current weight must be less than initial weight. All required fields are filled
    const requiredFieldErrors = [!name && 'name', !regDate && 'reg_date', !initialWeight && 'initial_weight'].filter(
      Boolean
    ) as Array<keyof Inventory>

    const isCurrentWeightMoreThanInitialWeight = currentWeight > initialWeight

    if (requiredFieldErrors.length === 0 && !isCurrentWeightMoreThanInitialWeight) {
      // if no errors, save

      props.onSave({
        ...props.beanInfo,
        name: name,
        cultivar: cultivar,
        farm: farm,
        region: region,
        country: country,
        exporter: exporter,
        importer: importer,
        producer: producer,
        elevation: elevation,

        bean_size: beanSize || null,
        drying_speed: dryingSpeed || null,
        bean_process: beanProcess || null,
        moisture: moisture,
        water_activity: waterActivity,
        density: density,
        green_bean_color: greenBeanColor,
        quality: quality,

        reg_date: regDate,
        current_weight: currentWeight,
        initial_weight: initialWeight,
        price: price,
        notes: notes,
      })
    } else {
      setRequiredFieldErrors(requiredFieldErrors)
      setIsCurrentWeightTooHigh(isCurrentWeightMoreThanInitialWeight)
    }
  }

  const removeRequiredFieldError = (propName: keyof Inventory): void => {
    if (requiredFieldErrors.indexOf(propName) > -1) {
      setRequiredFieldErrors((oldErrors) => oldErrors.filter((x) => x !== propName))
    }
  }

  const getRequiredFieldErrorMessage = (propName: keyof Inventory): string => {
    return requiredFieldErrors.indexOf(propName) > -1 ? t('inventory.requiredfield') : ''
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {isArchiveModalOpen && (
        <ConfirmationDialog
          header={t('inventory.movetoarchive')}
          body={t('inventory.movetoarchivebody')}
          confirmButtonText={t('inventory.movetoarchive')}
          onConfirm={() => {
            props.onSetArchived(true)
            setIsArchiveModalOpen(false)
          }}
          onClose={() => setIsArchiveModalOpen(false)}
        />
      )}
      <form>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={5}>
          <Stack spacing={2} flex={1}>
            <Typography sx={fieldsetHeaderStyle}>{t('inventory.origin')}</Typography>
            <TextInput
              label={t('inventory.name')}
              value={name}
              isRequired={true}
              isDisabled={areFieldsDisabled}
              autoFocus={true}
              errorMessage={getRequiredFieldErrorMessage('name')}
              onChange={(newValue: string) => {
                setName(newValue)
                removeRequiredFieldError('name')
              }}
            />
            <TextInput
              label={t('inventory.cultivar')}
              value={cultivar}
              isRequired={false}
              isDisabled={areFieldsDisabled}
              errorMessage={getRequiredFieldErrorMessage('cultivar')}
              onChange={(newValue: string) => {
                setCultivar(newValue)
                removeRequiredFieldError('cultivar')
              }}
            />
            <TextInput
              label={t('inventory.farm')}
              value={farm}
              isRequired={false}
              isDisabled={areFieldsDisabled}
              errorMessage={getRequiredFieldErrorMessage('farm')}
              onChange={(newValue: string) => {
                setFarm(newValue)
                removeRequiredFieldError('farm')
              }}
            />
            <TextInput
              label={t('inventory.region')}
              value={region}
              isRequired={false}
              isDisabled={areFieldsDisabled}
              errorMessage={getRequiredFieldErrorMessage('region')}
              onChange={(newValue: string) => {
                setRegion(newValue)
                removeRequiredFieldError('region')
              }}
            />
            <TextInput
              label={t('inventory.country')}
              value={country}
              isRequired={false}
              isDisabled={areFieldsDisabled}
              errorMessage={getRequiredFieldErrorMessage('country')}
              onChange={(newValue: string) => {
                setCountry(newValue)
                removeRequiredFieldError('country')
              }}
            />
            <TextInput
              label={t('inventory.exporter')}
              value={exporter}
              isRequired={false}
              isDisabled={areFieldsDisabled}
              errorMessage={getRequiredFieldErrorMessage('exporter')}
              onChange={(newValue: string) => {
                setExporter(newValue)
                removeRequiredFieldError('exporter')
              }}
            />
            <TextInput
              label={t('inventory.importer')}
              value={importer}
              isRequired={false}
              isDisabled={areFieldsDisabled}
              errorMessage={getRequiredFieldErrorMessage('importer')}
              onChange={(newValue: string) => {
                setImporter(newValue)
                removeRequiredFieldError('importer')
              }}
            />
            <TextInput
              label={t('inventory.producer')}
              value={producer}
              isRequired={false}
              isDisabled={areFieldsDisabled}
              errorMessage={getRequiredFieldErrorMessage('producer')}
              onChange={(newValue: string) => {
                setProducer(newValue)
                removeRequiredFieldError('producer')
              }}
            />
            <NumberField
              label={t('inventory.elevationlong')}
              numValue={elevation}
              disabled={areFieldsDisabled}
              valueSuffix="m"
              minNumValue={0}
              maxNumValue={8000}
              decimalPlaces={0}
              onBlur={(newValue: number) => {
                setElevation(newValue)
                removeRequiredFieldError('elevation')
              }}
            />
          </Stack>
          <Stack spacing={2} flex={1}>
            <Typography sx={fieldsetHeaderStyle}>{t('inventory.properties')}</Typography>
            <Select
              label={t('inventory.beansize')}
              id="bean_size-select"
              inventoryId={props.beanInfo.url}
              value={beanSize}
              isDisabled={areFieldsDisabled}
              errorMessage={getRequiredFieldErrorMessage('bean_size')}
              options={[
                { value: BeanSize.VeryLarge, label: t(BeanSizeString[BeanSize.VeryLarge]) },
                { value: BeanSize.Large, label: t(BeanSizeString[BeanSize.Large]) },
                { value: BeanSize.Medium, label: t(BeanSizeString[BeanSize.Medium]) },
                { value: BeanSize.Small, label: t(BeanSizeString[BeanSize.Small]) },
                { value: BeanSize.Shells, label: t(BeanSizeString[BeanSize.Shells]) },
              ]}
              setFn={(newValue: BeanSize) => {
                removeRequiredFieldError('bean_size')
                setBeanSize(newValue)
              }}
            />
            <Select
              label={t('inventory.dryingspeed')}
              id="drying_speed-select"
              inventoryId={props.beanInfo.url}
              value={dryingSpeed}
              isDisabled={areFieldsDisabled}
              errorMessage={getRequiredFieldErrorMessage('drying_speed')}
              options={[
                { value: DryingSpeed.Slow, label: t(DryingSpeedString[DryingSpeed.Slow]) },
                { value: DryingSpeed.Medium, label: t(DryingSpeedString[DryingSpeed.Medium]) },
                { value: DryingSpeed.Fast, label: t(DryingSpeedString[DryingSpeed.Fast]) },
                { value: DryingSpeed.Unknown, label: t(DryingSpeedString[DryingSpeed.Unknown]) },
              ]}
              setFn={(newValue: DryingSpeed) => {
                removeRequiredFieldError('drying_speed')
                setDryingSpeed(newValue)
              }}
            />
            <Select
              label={t('inventory.beanprocess')}
              id="bean_process-select"
              inventoryId={props.beanInfo.url}
              value={beanProcess}
              isDisabled={areFieldsDisabled}
              errorMessage={getRequiredFieldErrorMessage('bean_process')}
              options={[
                { value: Process.Natural, label: t(ProcessString[Process.Natural]) },
                { value: Process.Washed, label: t(ProcessString[Process.Washed]) },
                { value: Process.HoneyProcess, label: t(ProcessString[Process.HoneyProcess]) },
                { value: Process.Other, label: t(ProcessString[Process.Other]) },
              ]}
              setFn={(newValue: Process) => {
                removeRequiredFieldError('bean_process')
                setBeanProcess(newValue)
              }}
            />
            <NumberField
              label={t('inventory.moisture')}
              numValue={moisture}
              disabled={areFieldsDisabled}
              valueSuffix="%"
              minNumValue={0}
              maxNumValue={100}
              decimalPlaces={2}
              onBlur={(newValue: number) => {
                setMoisture(newValue)
                removeRequiredFieldError('moisture')
              }}
            />
            <NumberField
              label={t('inventory.wateractivity')}
              numValue={waterActivity}
              disabled={areFieldsDisabled}
              valueSuffix=""
              minNumValue={0}
              maxNumValue={1}
              decimalPlaces={2}
              onBlur={(newValue: number) => {
                setWaterActivity(newValue)
                removeRequiredFieldError('water_activity')
              }}
            />
            <NumberField
              label={t('inventory.density')}
              numValue={density}
              disabled={areFieldsDisabled}
              valueSuffix="g/ml"
              minNumValue={0}
              maxNumValue={5000}
              decimalPlaces={2}
              onBlur={(newValue: number) => {
                setDensity(newValue)
                removeRequiredFieldError('density')
              }}
            />
            <NumberField
              label={t('inventory.greenbeancolor')}
              numValue={greenBeanColor}
              disabled={areFieldsDisabled}
              valueSuffix=""
              minNumValue={0}
              maxNumValue={1000}
              decimalPlaces={2}
              onBlur={(newValue: number) => {
                setGreenBeanColor(newValue)
              }}
            />
            <FormControl disabled={areFieldsDisabled} sx={{ alignSelf: 'flex-start' }}>
              <InputLabel shrink>{t('inventory.quality')}</InputLabel>
              <Rating
                sx={{ mt: 1.5, color: 'primary.main' }}
                onChange={(_event, newValue: number | null) => setQuality(newValue || 0)}
                size="large"
                value={quality}
                disabled={areFieldsDisabled}
              />
            </FormControl>
          </Stack>
          <Stack spacing={2} flex={1}>
            <Typography sx={fieldsetHeaderStyle}>{t('inventory.storage')}</Typography>
            <DatePicker
              label={t('inventory.regdate')}
              inputFormat="dd/MM/yyyy"
              onChange={(newValue: Date | null) => {
                if (newValue && newValue.getTime()) {
                  removeRequiredFieldError('reg_date')
                  setRegDate(newValue)
                } else {
                  setRegDate(null)
                }
              }}
              value={regDate}
              disabled={areFieldsDisabled}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={!!getRequiredFieldErrorMessage('reg_date')}
                  helperText={getRequiredFieldErrorMessage('reg_date')}
                  required
                />
              )}
            />
            <Stack direction="row" spacing={2}>
              <Stack flex={1}>
                <NumberField
                  label={t('inventory.currentweight')}
                  numValue={currentWeight}
                  disabled={areFieldsDisabled}
                  valueSuffix={getBigWeightSuffix()}
                  minNumValue={0}
                  maxNumValue={999999}
                  decimalPlaces={3}
                  errorMessage={
                    (isCurrentWeightTooHigh && t('inventory.currentweighterror')) ||
                    getRequiredFieldErrorMessage('current_weight') ||
                    ''
                  }
                  onBlur={(newValue: number) => {
                    // remove current_weight message if validation error is fixed
                    if (isCurrentWeightTooHigh) {
                      const isCurrentWeightMoreThanInitialWeight = currentWeight > initialWeight
                      setIsCurrentWeightTooHigh(isCurrentWeightMoreThanInitialWeight)
                    }
                    removeRequiredFieldError('current_weight')
                    setCurrentWeight(newValue)
                  }}
                />
              </Stack>
              <Stack flex={1}>
                <NumberField
                  label={t('inventory.initialweight')}
                  numValue={initialWeight}
                  disabled={areFieldsDisabled}
                  valueSuffix={getBigWeightSuffix()}
                  minNumValue={0}
                  maxNumValue={999999}
                  decimalPlaces={3}
                  required={true}
                  errorMessage={getRequiredFieldErrorMessage('initial_weight')}
                  onBlur={(newValue: number) => {
                    removeRequiredFieldError('initial_weight')
                    setInitialWeight(newValue)
                  }}
                />
              </Stack>
            </Stack>
            <NumberField
              label={t('inventory.price')}
              numValue={price}
              disabled={areFieldsDisabled}
              valueSuffix={`$/${getSingleBigWeightSuffix()}`}
              minNumValue={0}
              maxNumValue={999999}
              decimalPlaces={2}
              onBlur={(newValue: number) => {
                setPrice(newValue)
                removeRequiredFieldError('price')
              }}
            />
            <TextInput
              label={t('inventory.notes')}
              value={notes}
              isDisabled={areFieldsDisabled}
              isRequired={false}
              multiline
              errorMessage={getRequiredFieldErrorMessage('notes')}
              onChange={(newValue: string) => {
                setNotes(newValue)
                removeRequiredFieldError('notes')
              }}
            />
          </Stack>
        </Stack>
        {(requiredFieldErrors.length > 0 || isCurrentWeightTooHigh) && (
          <Alert severity="error">{t('inventory.formvalidationerror')}</Alert>
        )}
        <Stack direction="row" justifyContent="flex-end" mt={4} spacing={2}>
          {!areFieldsDisabled && (
            <LoadingButton
              variant="contained"
              onClick={saveData}
              loading={props.isSaving}
              disabled={props.isSettingArchived}
            >
              {props.beanInfo.url ? t('inventory.update') : t('inventory.create')}
            </LoadingButton>
          )}
          {props.beanInfo.url && !props.beanInfo.is_archived && (
            <LoadingButton
              variant="outlined"
              onClick={() => setIsArchiveModalOpen(true)}
              loading={props.isSettingArchived}
              disabled={props.isSaving}
            >
              {t('inventory.setarchived')}
            </LoadingButton>
          )}
          {props.beanInfo.url && props.beanInfo.is_archived && (
            <LoadingButton
              variant="outlined"
              onClick={() => props.onSetArchived(false)}
              loading={props.isSettingArchived}
              disabled={props.isSaving}
            >
              {t('inventory.movetocurrent')}
            </LoadingButton>
          )}
        </Stack>
      </form>
    </LocalizationProvider>
  )
}

export default InventoryForm
